import axios from 'axios';

// Create axios instance with base URL from environment variables
const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000',
  headers: {
    'Content-Type': 'application/json',
  },
});

// API functions for chat
export const chatApi = {
  // Send a message to the chat endpoint (non-streaming)
  sendMessage: async (message, conversationId = null) => {
    try {
      const payload = { message };
      
      // Only include conversation_id if it's provided and not empty
      if (conversationId) {
        payload.conversation_id = conversationId;
      }
      
      const response = await api.post('/api/chat', payload);
      return response.data;
    } catch (error) {
      console.error('Error sending chat message:', error);
      throw error;
    }
  },

  /**
   * Send a message and receive streaming response via SSE
   * @param {string} message - The user message
   * @param {string|null} conversationId - Optional conversation ID
   * @param {object} callbacks - Callback functions for stream events
   * @param {function} callbacks.onText - Called with text delta
   * @param {function} callbacks.onToolCallStarted - Called when tool call starts
   * @param {function} callbacks.onToolResult - Called with tool result
   * @param {function} callbacks.onDone - Called when stream completes
   * @param {function} callbacks.onError - Called on error
   * @param {function} callbacks.onConversationId - Called with conversation ID
   * @returns {function} Abort function to cancel the stream
   */
  sendMessageStream: (message, conversationId, callbacks) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
    const abortController = new AbortController();

    const fetchStream = async () => {
      try {
        const response = await fetch(`${baseUrl}/api/chat/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message,
            conversation_id: conversationId || null,
          }),
          signal: abortController.signal,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Stream request failed');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));

                switch (data.type) {
                  case 'conversation_id':
                    callbacks.onConversationId?.(data.conversation_id);
                    break;
                  case 'text':
                    callbacks.onText?.(data.content);
                    break;
                  case 'tool_call_started':
                    callbacks.onToolCallStarted?.(data.name);
                    break;
                  case 'tool_result':
                    callbacks.onToolResult?.(data.name, data.citations);
                    break;
                  case 'generating':
                    callbacks.onGenerating?.();
                    break;
                  case 'done':
                    callbacks.onDone?.(data.citations, data.token_usage);
                    break;
                  case 'error':
                    callbacks.onError?.(data.error);
                    break;
                  default:
                    break;
                }
              } catch (parseError) {
                console.error('Error parsing SSE data:', parseError);
              }
            }
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('Stream aborted');
        } else {
          callbacks.onError?.(error.message);
        }
      }
    };

    fetchStream();

    // Return abort function
    return () => abortController.abort();
  },
  
  // Export a message as Markdown or DOCX
  exportMessageAsPdf: async (messageContent, messageId = null, conversationId = null, citations = [], format = 'md') => {
    try {
      // Format the filename based on date and message ID
      const date = new Date().toISOString().split('T')[0];
      const generatedFilename = `karlb-message-${messageId || date}`;
      
      // Create payload matching backend expectations
      const payload = { 
        text: messageContent,
        format: format.toLowerCase(), // Use the format parameter
        filename: generatedFilename,
        citations: citations
      };
      
      // Add message and conversation IDs if available
      if (messageId) {
        payload.message_id = messageId;
      }
      
      if (conversationId) {
        payload.conversation_id = conversationId;
      }
      
      // Make the request with responseType blob to handle PDF
      const response = await api.post('/api/export', payload, {
        responseType: 'blob'
      });
      
      // Determine content type and file extension based on format
      let contentType, fileExtension;
      
      if (format.toLowerCase() === 'md' || format.toLowerCase() === 'markdown') {
        contentType = 'text/markdown';
        fileExtension = 'md';
      } else if (format.toLowerCase() === 'docx') {
        contentType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        fileExtension = 'docx';
      } else {
        // Default to markdown
        contentType = 'text/markdown';
        fileExtension = 'md';
      }
      
      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Use the filename from the payload with appropriate extension
      link.setAttribute('download', `${generatedFilename}.${fileExtension}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error(`Error exporting message as ${format}:`, error);
      throw error;
    }
  },
};

// API functions for settings
export const settingsApi = {
  // Get settings
  getSettings: async () => {
    try {
      const response = await api.get('/api/settings');
      return response.data;
    } catch (error) {
      console.error('Error fetching settings:', error);
      throw error;
    }
  },
};

// Export the axios instance for direct use
export default api;
